/**
 * Authentication API calls.
 *
 * Mirrors the legacy AngularJS loginComponent.js and auth module endpoints.
 * Sign-in itself now goes exclusively through brewauth OIDC (see
 * `@/auth/oidc`); signup and password reset are owned by brewauth too
 * (`/ui/signup`, `/ui/reset`). The server-side `logoff` call that used to
 * live here is gone (jorigin/brewauth#99): the Java endpoint it hit was a
 * no-op once brewauth owned sessions and token revocation, and sign-out is
 * brewauth's `end_session` (see AuthContext's handleLogout). What's left
 * here is the info endpoints.
 */
import apiClient from './client';
import type { SystemInfo } from '@/types';

/** Fetch system info (license, auth config, CAPCO). */
export async function fetchSystemInfo(): Promise<SystemInfo> {
  const res = await apiClient.get<SystemInfo>('api/info');
  return res.data;
}

/** Helper function to determine displayToUser flag. */
function resolveDisplayToUser(displayToUser: unknown): boolean {
  if (typeof displayToUser === 'boolean') return displayToUser;
  if (typeof displayToUser === 'string') return displayToUser.toLowerCase() === 'true';
  return false;
}

/** Fetch previous-login configuration (for login notification). */
export async function fetchPreviousLoginConfig(): Promise<{
  displayToUser: boolean;
  ttl: number;
  messageTemplate: string;
}> {
  const res = await apiClient.get('/api/info');
  const confs = typeof res.data.previousLogin === 'string'
      ? JSON.parse(res.data.previousLogin)
      : res.data.previousLogin;

  return {
    displayToUser: resolveDisplayToUser(confs.displayToUser),
    ttl: confs.ttl ? Number(confs.ttl) : -1,
    messageTemplate:
        confs.messageTemplate ??
        'Login successful. Previous login date was {{previousLoginDate}}',
  };
}
